/**
 * Shared "is this request bound for a HOGC-owned API?" test.
 *
 * Both axios interceptors need it and must agree: auth-header only rewrites
 * headers on our own hosts, and 401-error only refreshes-and-retries on our own
 * hosts. Two copies of this rule drifting apart would mean a request whose header
 * one interceptor owns and the other doesn't.
 */

const getHogcApiOrigins = () => {
  const origins = new Set();
  const urls = [
    process.env.VUE_APP_API_URL,
    process.env.VUE_APP_GIVING_API_URL,
    process.env.VUE_APP_GIVING_API_V2_URL,
  ];
  for (const url of urls) {
    if (!url) continue;
    try {
      origins.add(new URL(url).origin);
    } catch (_) {
      // ignore malformed env URL
    }
  }
  return origins;
};

/**
 * @param {string} [requestUrl] - axios config.url (may be relative to baseURL)
 * @param {string} [baseURL] - axios config.baseURL
 * @returns {boolean} true when the resolved request targets a HOGC API host
 */
export const isHogcApiUrl = (requestUrl, baseURL) => {
  if (!requestUrl && !baseURL) return false;
  const origins = getHogcApiOrigins();
  if (origins.size === 0) return false;
  try {
    // axios may receive a relative URL + a baseURL; resolve against baseURL.
    const resolved = new URL(requestUrl || '', baseURL || undefined);
    return origins.has(resolved.origin);
  } catch (_) {
    // If URL can't be parsed (e.g. relative URL with no baseURL), fall back to
    // substring match against configured hosts to avoid false positives.
    for (const origin of origins) {
      if (typeof requestUrl === 'string' && requestUrl.startsWith(origin)) return true;
    }
    return false;
  }
};
